import type Lenis from 'lenis';

/**
 * Track if a smooth scroll animation is currently in progress
 * This helps prevent multiple scroll animations from competing
 */
let isScrollAnimationActive = false;
let lenisInstance: Lenis | null = null;

/**
 * Set the Lenis instance for use in scroll utilities
 * @param instance - The Lenis instance
 */
export const setLenisInstance = (instance: Lenis | null): void => {
  lenisInstance = instance;
};

/**
 * Smoothly scrolls to an element by ID with offset for fixed header
 * Uses Lenis for smooth scrolling if available, falls back to native smooth scroll
 * Includes protection against competing scroll animations
 * Properly handles different offsets for mobile and desktop
 * @param elementId - The ID of the element to scroll to
 */
export const smoothScrollTo = (elementId: string): void => {
  // Don't start a new scroll animation if one is already in progress
  if (isScrollAnimationActive) return;
  
  const element = document.getElementById(elementId);
  if (!element) return;

  // Mark that we're starting a scroll animation
  isScrollAnimationActive = true;
  
  // Detect if we're on mobile by checking viewport width
  const isMobile = window.innerWidth < 768; // Standard mobile breakpoint
  
  // Get header height to offset scroll position - specifically measure the correct header
  // based on device type (header might have different height on mobile)
  const header = document.querySelector('header');
  const headerHeight = header?.offsetHeight || 0;
  
  // Calculate a more precise mobile offset based on potential UI differences
  let mobileAdjustment = 0;
  
  if (isMobile) {
    // On mobile, add more specific adjustments if needed
    // For example, account for bottom navigation, different padding, etc.
    mobileAdjustment = 60; // Larger offset for mobile (adjusted from previous 20px)
    
    // You can add more specific measurements here if needed:
    // const mobileNavHeight = document.querySelector('.mobile-specific-nav')?.offsetHeight || 0;
    // mobileAdjustment += mobileNavHeight;
  }
  
  // Different base padding for mobile vs desktop
  const basePadding = isMobile ? 30 : 40;
  const totalOffset = headerHeight + basePadding + mobileAdjustment;
  
  // Use Lenis if available, otherwise fall back to native smooth scroll
  if (lenisInstance) {
    // Use Lenis for smooth scrolling
    lenisInstance.scrollTo(element, {
      offset: -totalOffset,
      duration: 1.2,
      onComplete: (): void => {
        // Ensure focus is managed properly for accessibility
        element.setAttribute('tabindex', '-1');
        element.focus({ preventScroll: true });
        
        // Release the animation lock
        isScrollAnimationActive = false;
      }
    });
  } else {
    // Fall back to native smooth scrolling
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const offsetPosition = absoluteElementTop - totalOffset;
    
    // Use smooth scroll with custom timing
    const startPosition = window.pageYOffset;
    const distance = offsetPosition - startPosition;
    const duration = Math.min(1000, Math.max(500, Math.abs(distance) * 0.5));
  
    // Perform the scroll
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Ensure focus is managed properly for accessibility
    // And release the scrolling lock when animation completes
    setTimeout((): void => {
      element.setAttribute('tabindex', '-1');
      element.focus({ preventScroll: true });
      
      // Release the animation lock
      isScrollAnimationActive = false;
    }, duration);
  }
};

/**
 * Navigate to a new page and ensure it starts from the top
 * @param path - The path to navigate to
 */
export const navigateToPage = (path: string): void => {
  // Force scroll to top before navigation
  if (lenisInstance) {
    lenisInstance.scrollTo(0, { immediate: true });
  } else {
    window.scrollTo(0, 0);
  }
  
  // Perform the navigation
  window.location.href = path;
};
